import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { getYouTubeDriver } from '../utils/youtubePlayer';
import { registerWakeHandler } from '../utils/wakeRecovery';
import { useScreenWakeLock } from '../utils/useScreenWakeLock';
import { createBackgroundInterval } from '../utils/backgroundTimer';

import {
  startPlaybackWatchdog,
  startWakeLockLossLogger,
  startNetworkRecovery,
} from '../utils/stabilityWatchdogs';

/**
 * MusicContext — YouTube-backed playback orchestrator (Feb 2026).
 *
 * Replaces the former Apple MusicKit engine with a dual YouTube IFrame
 * driver (`utils/youtubePlayer.js`) that runs two hidden <iframe> players,
 * crossfades 10s starting 15s before end, and swaps roles after.
 *
 * WebSocket command bus and backend playback_queue REST are unchanged —
 * play/pause/skip/setVolume proxy to the active YouTube player.
 */

const MusicContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MusicProvider = ({ children }) => {
  const { user } = useAuth();
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [progressMs, setProgressMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [showPlayer, setShowPlayer] = useState(false);
  const [queue, setQueueState] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  // Which playlist the live queue came from. null = unattributed (e.g. a
  // queue built from guest requests). The Booth needs it to keep its
  // playlist picker and its song list describing the same playlist.
  const [queuePlaylistId, setQueuePlaylistId] = useState(null);
  const [nextUp, setNextUp] = useState(null);
  const [crossfadeActive, setCrossfadeActive] = useState(false);
  const [activeSlot, setActiveSlot] = useState('A');
  const [volume, setVolumeState] = useState(100);
  const [adPlaying, setAdPlaying] = useState(false);

  // ---- Background playback (Jun 2026 — matched to what actually works) ----
  // IMPORTANT FINDING: a plain YouTube IFrame keeps playing in a backgrounded
  // Android tab on its OWN — the iframe's <video> holds the OS media session.
  // Our previous "hardening" layered a silent <audio> anchor + a WebAudio
  // keep-alive tone ON TOP of the iframe; those extra always-playing audio
  // sources STOLE audio focus / the media session from the YouTube iframe, so
  // when the tab was backgrounded Android kept the (silent) anchor alive and
  // SUSPENDED the real YouTube audio. Working reference players (e.g.
  // deluxesalonplaylist.in) use the bare iframe with no anchor/tone and keep
  // playing. So we deliberately run NO silent anchor and NO keep-alive tone
  // here — the YouTube iframe is the sole audio source. We keep ONLY the
  // screen wake-lock (keeps a foregrounded tablet's screen on; no audio focus
  // impact) and the resume-on-foreground handler below.
  useScreenWakeLock(isPlaying);

  // When `playTracks` is called before the driver has finished its async
  // init, we can't call `.play()` yet — but the backend queue is already
  // set. Flag it here so the driver-hydrate effect (below) knows to
  // start playback the moment the driver becomes ready. Cleared after
  // consumption. Feb 2026 — fixes "Play button silently no-ops when
  // clicked from Playlist detail page before Home has mounted the driver".
  const pendingAutoplayRef = useRef(false);
  // Signature of the queue we last hydrated the driver with. Set inside
  // the hydration effect below; used to skip redundant loadQueue calls
  // that would silently reset the YT.Player from PLAYING → CUED and
  // cancel our initial play() (the exact race that made every
  // embeddable playlist sit at state=5 forever — see iteration_84 RCA).
  const lastHydratedSigRef = useRef('');
  const driverRef = useRef(null);
  const queueRef = useRef([]);
  // Tracks the currently-playing track id so we can fire `/requests/mark-played`
  // the moment the active track advances (crossfade, natural end, or skip) —
  // this clears the guest-request row from the home-screen inbox once its
  // song has played, instead of letting it linger until the 24h sweep.
  const playedTrackRef = useRef(null);
  // Latest playlist id backing the live queue + a guard/cooldown so the
  // end-of-playlist advance (onQueueExhausted) can't runaway-loop if several
  // playlists in a row fail to load.
  const queuePlaylistIdRef = useRef(null);
  const advancingRef = useRef(false);
  const lastAdvanceAtRef = useRef(0);
  const exhaustRef = useRef(() => {});

  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { queuePlaylistIdRef.current = queuePlaylistId; }, [queuePlaylistId]);

  // --- Driver init ---
  useEffect(() => {
    const driver = getYouTubeDriver();
    driverRef.current = driver;

    driver.init({
      fadeTriggerSec: 15,
      fadeDurationMs: 10000,
      callbacks: {
        onState: ({ isPlaying: p, progressMs: pm, durationMs: dm, activeTrack, nextTrack, activeSlot: as }) => {
          // NOTE: no `cancelled` guard here. React StrictMode double-mounts
          // this provider; the first cleanup would flip cancelled=true and
          // silently break every subsequent state update because the driver
          // is a module-scoped singleton. React 18 already no-ops setState
          // on unmounted components, so an unconditional callback is safe.
          setIsPlaying(p);
          setProgressMs(pm);
          setDurationMs(dm);
          if (activeTrack) {
            // When the active track advances, mark the FINISHED track's guest
            // request(s) as played so they drop out of the home-screen inbox
            // right away instead of lingering until the 24h stale sweep.
            const curId = activeTrack.apple_id || activeTrack.id || null;
            const prev = playedTrackRef.current;
            const prevId = prev?.apple_id || prev?.id || null;
            if (prevId && curId && prevId !== curId) {
              axios.post(`${API}/requests/mark-played`, { apple_id: prevId }).catch(() => {});
            }
            if (prevId !== curId) playedTrackRef.current = activeTrack;
            setCurrentTrack(activeTrack);
          }
          if (nextTrack) setNextUp(nextTrack);
          setActiveSlot(as);
          // Detect crossfade window
          if (dm > 0 && activeTrack && (dm - pm) <= 10000 && (dm - pm) > 0 && nextTrack) {
            setCrossfadeActive(true);
          } else {
            setCrossfadeActive(false);
          }
        },
        onTrackChanged: (active, next) => {
          setCurrentTrack(active);
          setNextUp(next);
          setCrossfadeActive(false);
        },
        onTrackDone: async (_finishedIdx) => {
          try { await axios.post(`${API}/queue/next`); } catch { /* noop */ }
        },
        onEnded: () => {
          setIsPlaying(false);
        },
        onQueueExhausted: () => { exhaustRef.current?.(); },
        onAdDetected: ({ expected, seen } = {}) => {
          // A YouTube ad slipped through (usually a pre-roll on the very
          // first play, or occasionally mid-set). The driver has already
          // muted the player; we surface a pill so the operator knows
          // "the tablet is briefly silent because YouTube is showing an
          // ad, the real track will resume in a few seconds".
          setAdPlaying(true);
          // Telemetry: fire-and-forget POST so the Forensics UI can chart
          // ad-slip rate per venue AND identify which specific tracks
          // burn the most ads (feeds the Topic-only filter's tuning).
          try {
            const cur = heartbeatRef.current?.currentTrack || null;
            axios.post(`${API}/telemetry/ad-detected`, {
              requested_video_id: expected || cur?.id || '',
              actual_video_id: seen || '',
              track_name: (cur?.name || '').slice(0, 200),
              track_artists: (Array.isArray(cur?.artists) ? cur.artists.join(', ') : (cur?.artists || '')).slice(0, 200),
            }).catch(() => { /* telemetry never blocks playback */ });
          } catch { /* noop */ }
        },
        onAdEnded: () => {
          setAdPlaying(false);
        },
      },
    }).then(() => {
      setPlayerReady(true);
    }).catch((err) => {
      console.error('[MusicContext] YouTube driver init failed:', err);
    });

    return () => { /* singleton driver survives — nothing to clean up */ };
  }, []);

  // --- Backend queue sync ---
  const refreshQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/queue/current`);
      const tracks = res.data.tracks || [];
      setQueueState(tracks);
      setQueueIndex(res.data.current_index || 0);
      setQueuePlaylistId(res.data.playlist_id || null);
      setNextUp(res.data.next_track || null);
      return res.data;
    } catch { return null; }
  }, []);

  // Poll backend queue every 5s while logged in (WS bus handles instant commands)
  useEffect(() => {
    if (!user) return undefined;
    refreshQueue();
    const id = setInterval(refreshQueue, 5000);
    return () => clearInterval(id);
  }, [user, refreshQueue]);

  // Long-idle wake recovery. When Android backgrounds the tab for >5 min
  // and the operator brings it back to foreground, the WS is often
  // disconnected and the queue view is stale. Re-hydrate silently instead
  // of forcing a manual refresh.
  useEffect(() => {
    if (!user) return undefined;
    return registerWakeHandler(async () => {
      try {
        await refreshQueue();
      } catch { /* noop */ }
      // Force one heartbeat immediately so the TV wallpaper (on another
      // device) resyncs without waiting for the next 2s tick.
      const s = heartbeatRef.current;
      const vid = s?.currentTrack?.id || null;
      if (vid) {
        axios.post(`${API}/playback/heartbeat`, {
          is_playing: !!s.isPlaying,
          progress_ms: Math.max(0, Math.floor(s.progressMs || 0)),
          video_id: vid,
        }).catch(() => { /* silent */ });
      }
    });
  }, [user, refreshQueue]);

  // Instant resume-on-foreground (Jun 2026). YouTube enforces background
  // pause SERVER-SIDE (since Jan 2026): once the Android browser is
  // minimised / another app is opened / the screen locks, the embedded
  // player is paused and NO web trick (MediaSession, silent-audio anchor,
  // visibilitychange→playVideo) can keep it running while hidden. What we
  // CAN guarantee is that the moment the venue returns to the booth,
  // playback resumes immediately from where it left off — without waiting
  // for the (throttled-while-hidden) worker tick to notice. We only resume
  // when the operator INTENDED to be playing (`driver._playIntent`), so a
  // deliberate pause is never overridden.
  useEffect(() => {
    if (!user) return undefined;
    const resumeIfIntended = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      const driver = driverRef.current;
      if (driver && driver._playIntent) {
        try { driver.play(); } catch { /* noop */ }
      }
    };
    document.addEventListener('visibilitychange', resumeIfIntended);
    window.addEventListener('focus', resumeIfIntended);
    window.addEventListener('pageshow', resumeIfIntended);
    return () => {
      document.removeEventListener('visibilitychange', resumeIfIntended);
      window.removeEventListener('focus', resumeIfIntended);
      window.removeEventListener('pageshow', resumeIfIntended);
    };
  }, [user]);


  // Hydrate the driver whenever the backend queue changes and doesn't match
  // what the driver currently holds.
  useEffect(() => {
    if (!queue.length) return;
    // Filter tracks to only valid YouTube 11-char videoIds. Legacy Apple
    // numeric ids (from pre-YouTube-pivot playlists) can't play — mirroring
    // the guard in playTracks so callers that go via /api/queue/load
    // instead of playTracks get the same protection.
    const isValidYtId = (s) => typeof s === 'string' && /^[A-Za-z0-9_-]{11}$/.test(s);
    const norm = queue.map(t => {
      const raw = t?.id || t?.youtube_id || t?.videoId || t?.apple_id;
      return { ...t, id: isValidYtId(raw) ? raw : null };
    }).filter(t => t.id);
    if (!norm.length) {
      const anyRaw = (queue[0]?.id || queue[0]?.apple_id || '');
      if (typeof anyRaw === 'string' && /^\d+$/.test(anyRaw)) {
        toast.error('This playlist has legacy Apple ids — please re-import from YouTube.');
      }
      return;
    }
    const clampedIndex = Math.min(queueIndex, norm.length - 1);
    // Seed the on-screen "now playing" from the backend index ONLY while the
    // live driver isn't yet the source of truth (not ready, or holds no
    // queue). Once the driver is playing, its onState / onTrackChanged own
    // `currentTrack` — letting the 5s backend poll overwrite it here made the
    // album art flip to the PREVIOUS song whenever the backend current_index
    // lagged the live driver (e.g. a dropped /queue/next POST during a
    // crossfade left the backend index stuck one behind the audio).
    const driverLive = playerReady && driverRef.current && (driverRef.current.tracks?.length > 0);
    if (!driverLive) {
      const t = norm[clampedIndex] || norm[0];
      if (t) {
        setCurrentTrack(t);
        setNextUp(norm[clampedIndex + 1] || null);
      }
    }
    setShowPlayer(true);
    if (!playerReady || !driverRef.current) return;
    // De-dupe hydration to avoid clobbering the driver mid-play (iter 84
    // regression). Signature = length + first + last + startIndex — if
    // the driver already holds this exact queue+cursor, skip the whole
    // effect. Without this, the effect fires ~1-2s after playTracks and
    // re-invokes loadQueue → cueVideoById → resets state to CUED(5) →
    // silently cancels the play() call we made moments earlier.
    const sig = `${norm.length}|${norm[0]?.id || ''}|${norm[norm.length - 1]?.id || ''}|${clampedIndex}`;
    if (sig === lastHydratedSigRef.current) {
      // Nothing to hydrate — but a pending autoplay flag can still be
      // waiting from playTracks (driver was un-ready when Play clicked,
      // now becoming ready with the same queue already loaded).
      if (pendingAutoplayRef.current) {
        pendingAutoplayRef.current = false;
        try { driverRef.current.play(); } catch { /* noop */ }
      }
      return;
    }
    lastHydratedSigRef.current = sig;
    // pendingAutoplayRef must be consumed BEFORE the same-track early-
    // return below — otherwise if the driver already has our target
    // track cued (e.g. from a prior /queue/current poll) and the operator
    // hits Play, the flag gets dropped and playback never starts.
    const consumePendingAutoplay = () => {
      if (pendingAutoplayRef.current) {
        pendingAutoplayRef.current = false;
        try { driverRef.current.play(); } catch { /* noop */ }
      }
    };
    // ---- Reconcile backend queue → live driver (June 2026 loop fix) -------
    // The driver is authoritative for the PLAYBACK POSITION; the backend
    // queue is authoritative for the track LIST. The 5s /queue/current poll
    // can momentarily report a current_index that LAGS the driver: the
    // driver advances at crossfade-finalize and only THEN posts /queue/next,
    // so a poll landing in that in-flight window returns the previous index.
    // Reloading the driver to that stale index reset playback to an earlier
    // track mid-song — the "jumps between songs / video restarts / stuck in
    // a loop / confused what to play" bug. Fix: never reload on index alone;
    // only reload when the track LIST genuinely changed AND the playing
    // track is gone.
    const driver = driverRef.current;
    // Don't reconcile mid-crossfade: the active fade already captured its
    // next track and does `currentIndex += 1` at finalize. Rewriting
    // currentIndex or reloading the queue now would desync the pointer from
    // the audio actually playing (off-by-one skip/repeat). The next 5s poll,
    // after the ~10s fade completes, reconciles cleanly.
    if (driver._fade) {
      consumePendingAutoplay();
      return;
    }
    const driverTracks = driver.tracks || [];
    const driverIds = driverTracks.map((t) => t.id);
    const normIds = norm.map((t) => t.id);
    const sameList =
      driverIds.length === normIds.length &&
      driverIds.every((id, i) => id === normIds[i]);
    const currentDriverId = driverTracks[driver.currentIndex]?.id;

    if (currentDriverId && sameList) {
      // Identical list — keep the driver's own live index, just refresh the
      // track metadata objects. No cue/load, so playback is never disturbed.
      driver.tracks = norm;
      consumePendingAutoplay();
      return;
    }

    if (currentDriverId) {
      // List changed (guest inject / reorder / edited playlist). If the track
      // playing right now still exists in the new list, adopt its new index
      // WITHOUT restarting it; only re-cue the idle player with the new next.
      const pos = normIds.indexOf(currentDriverId);
      if (pos >= 0) {
        driver.tracks = norm;
        driver.currentIndex = pos;
        const nextTrackId = norm[pos + 1]?.id;
        if (nextTrackId && driver._prewarmedFor !== nextTrackId) {
          try {
            const idle = driver._idlePlayer();
            idle.setVolume(0);
            idle.cueVideoById(nextTrackId);
            driver._prewarmedFor = nextTrackId;
          } catch { /* noop */ }
        }
        consumePendingAutoplay();
        return;
      }
    }

    // Genuinely new queue (fresh playlist, or the playing track was removed).
    // eslint-disable-next-line no-console
    console.log('[MusicContext] hydrating driver with', norm.length, 'tracks');
    driver.loadQueue(norm, clampedIndex);
    consumePendingAutoplay();
  }, [queue, queueIndex, playerReady]);

  // --- Public playback controls ---
  const playTracks = useCallback(async (tracks, startIndex = 0, opts = {}) => {
    if (!tracks?.length) return false;
    // Normalize + hydrate SYNCHRONOUSLY on the click-gesture so browser
    // autoplay policy accepts the first playVideo() call.
    // Legacy Apple numeric ids (all-digit strings) can't play on YouTube.
    // We filter those out and surface a toast so the operator knows the
    // playlist needs to be re-imported from YouTube.
    const isValidYtId = (s) => typeof s === 'string' && /^[A-Za-z0-9_-]{11}$/.test(s);
    const norm = tracks.map(t => {
      const raw = t?.id || t?.youtube_id || t?.videoId || t?.apple_id;
      return { ...t, id: isValidYtId(raw) ? raw : null };
    }).filter(t => t.id);
    const droppedLegacy = tracks.length - norm.length;
    if (!norm.length) {
      const anyRaw = (tracks[0]?.id || tracks[0]?.apple_id || '');
      if (typeof anyRaw === 'string' && /^\d+$/.test(anyRaw)) {
        toast.error('This playlist has legacy Apple ids — please re-import from YouTube.');
      } else {
        toast.error('No playable tracks in this playlist');
      }
      return false;
    }
    if (droppedLegacy > 0) {
      toast.info(`Skipping ${droppedLegacy} legacy track${droppedLegacy === 1 ? '' : 's'} — playing the rest.`);
    }
    const clampedStart = Math.min(startIndex, norm.length - 1);

    // Backend queue is authoritative — POST unconditionally so the
    // Home page hydrates from it when the operator lands there (fixes
    // the "click Play on Playlist detail → nothing happens" bug: the
    // driver isn't ready on non-Home routes, but backend must still
    // capture the intent).
    axios.post(`${API}/queue/load`, {
      tracks: norm,
      start_index: clampedStart,
      playlist_id: opts.playlist_id || opts.playlistId || null,
      playlist_type: opts.playlist_type || null,
    }).catch((e) => {
      console.warn('[MusicContext] queue/load failed:', e?.message);
    });

    // Optimistically update local state so the UI reflects the new queue
    // immediately (works even before the /queue/load POST returns).
    setQueueState(norm);
    setQueueIndex(clampedStart);
    setQueuePlaylistId(opts.playlist_id || opts.playlistId || null);
    setShowPlayer(true);
    // Reflect the chosen start track immediately so the Master Player card
    // doesn't flash "Nothing playing" before the driver's first onState tick.
    setCurrentTrack(norm[clampedStart] || norm[0] || null);
    setNextUp(norm[clampedStart + 1] || null);

    if (driverRef.current && playerReady) {
      // Driver ready — drive local playback right now on the click gesture.
      driverRef.current.loadQueue(norm, clampedStart);
      driverRef.current.play();
      // Stamp the signature so the queue-watching hydration effect
      // doesn't re-fire loadQueue on the state update we're about to
      // trigger below (setQueueState). This is the fix for the
      // iteration-84 hydration-race that reset embeddable players to
      // CUED forever.
      lastHydratedSigRef.current = `${norm.length}|${norm[0]?.id || ''}|${norm[norm.length - 1]?.id || ''}|${clampedStart}`;
    } else {
      // Driver ref exists but YT.Player is still async-booting (or hasn't
      // been mounted yet on this route). Flag autoplay so the hydrate
      // effect kicks off `.play()` the moment `playerReady` flips true.
      // Note: driverRef.current is assigned synchronously at mount but
      // playerReady only flips true after driver.init() resolves — so
      // gating on playerReady (not just driverRef.current) is the
      // difference between working and silently no-op.
      pendingAutoplayRef.current = true;
    }
    return true;
  }, [playerReady]);

  // End of the current playlist → roll into the next SCHEDULED playlist
  // (e.g. Tuesday-afternoon exhausted → Tuesday-evening). Walks the venue's
  // weekly timetable via /playlists/next-after, loads that playlist's tracks,
  // and crossfades straight in — so a staffed 8–10h shift never goes silent
  // and never just loops the same playlist. Last-resort: if the venue has NO
  // schedule at all, replay the current queue so the room stays live.
  const advanceToNextPlaylist = useCallback(async () => {
    if (advancingRef.current) return;
    // Only auto-advance when we actually intend to keep playing.
    if (driverRef.current && driverRef.current._playIntent === false) { setIsPlaying(false); return; }
    // Cooldown so a run of failing playlists can't tight-loop.
    if (Date.now() - lastAdvanceAtRef.current < 8000) return;
    advancingRef.current = true;
    lastAdvanceAtRef.current = Date.now();
    try {
      const curId = queuePlaylistIdRef.current;
      let nextPl = null;
      let nextTracks = null;
      try {
        const na = await axios.get(`${API}/playlists/next-after`, { params: { playlist_id: curId || '' } });
        nextPl = na.data?.playlist || null;
        if (nextPl?.id && nextPl.id !== curId) {
          const full = await axios.get(`${API}/playlists/curated/${nextPl.id}`);
          nextTracks = full.data?.tracks || [];
        } else if (nextPl?.id && nextPl.id === curId) {
          // Next scheduled slot is the SAME playlist (e.g. it's the only one
          // on the timetable) — reload it fresh so the room keeps playing.
          const full = await axios.get(`${API}/playlists/curated/${nextPl.id}`);
          nextTracks = full.data?.tracks || [];
        }
      } catch { /* fall through to replay */ }

      if (nextTracks && nextTracks.length) {
        if (nextPl?.name) toast.info(`Up next: ${nextPl.name}`);
        await playTracks(nextTracks, 0, { playlist_id: nextPl.id, playlist_type: 'curated' });
        return;
      }
      // No schedule to advance into — replay the current queue as a last
      // resort so the venue never falls silent.
      const cur = queueRef.current;
      if (cur && cur.length && driverRef.current) {
        driverRef.current.loadQueue(cur, 0);
        driverRef.current.play();
      } else {
        setIsPlaying(false);
      }
    } finally {
      advancingRef.current = false;
    }
  }, [playTracks]);

  useEffect(() => { exhaustRef.current = advanceToNextPlaylist; }, [advanceToNextPlaylist]);

  const play = useCallback(() => {
    driverRef.current?.play();
    setShowPlayer(true);
  }, []);
  const pause = useCallback(() => driverRef.current?.pause(), []);
  const nextTrack = useCallback(async () => {
    driverRef.current?.next();
    try { await axios.post(`${API}/queue/next`); } catch { /* noop */ }
  }, []);
  const previousTrack = useCallback(async () => {
    driverRef.current?.previous();
    try { await axios.post(`${API}/queue/previous`); } catch { /* noop */ }
  }, []);
  const setVolume = useCallback((v) => {
    const n = Math.max(0, Math.min(100, Math.round(v)));
    setVolumeState(n);
    driverRef.current?.setVolume(n);
  }, []);

  const injectNext = useCallback(async (track) => {
    if (!track) return;
    // Anchor the insert to the track the DRIVER is actually playing — not the
    // backend current_index, which can drift one behind the live audio if a
    // /queue/next advance POST was ever dropped. During a crossfade the
    // incoming track (currentIndex + 1) is the one becoming current, so anchor
    // to it; this keeps the injected song landing AFTER whatever the venue is
    // about to hear, never before it (the "added song never plays" bug).
    const d = driverRef.current;
    let afterVideoId = null;
    if (d && Array.isArray(d.tracks) && d.tracks.length) {
      const liveIdx = d._fade ? d.currentIndex + 1 : d.currentIndex;
      afterVideoId = d.tracks[liveIdx]?.id || d.tracks[d.currentIndex]?.id || null;
    }
    try {
      await axios.post(`${API}/queue/inject`, { track, after_video_id: afterVideoId });
      await refreshQueue();
      toast.success(`Added "${track.name}" up next`);
    } catch (e) {
      toast.error('Could not add track to queue');
    }
  }, [refreshQueue]);

  // --- Playback heartbeat ---------------------------------------------------
  // Push our current playback state to the backend every ~2s so the public
  // TV wallpaper (a different device on venue WiFi) can sync its muted
  // YouTube video's play/pause + seek to what the Booth is actually
  // playing. See routes/playback.py + TVNowPlayingArt.js for the read side.
  const heartbeatRef = useRef({ isPlaying, progressMs, currentTrack });
  useEffect(() => { heartbeatRef.current = { isPlaying, progressMs, currentTrack, host_id: user?.id || null }; }, [isPlaying, progressMs, currentTrack, user]);

  // Expose an "is anything live?" flag on window for the SW auto-reset and
  // the memory-pressure watchdog to defer disruptive reloads mid-track.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__spinItUpIsLive = !!(isPlaying || crossfadeActive);
    }
  }, [isPlaying, crossfadeActive]);

  // Presence + playback-hours heartbeat for COMMERCIAL venues. Previously this
  // lived only in AdminLayout, so the Control Booth (which uses <Layout/>, not
  // AdminLayout) never pinged while a venue sat there playing — "Hours online"
  // stayed at 0. MusicProvider is mounted app-wide, so firing it here covers
  // every commercial page. AI venues are handled by AiPlayerContext (gated
  // below so we never double-count).
  useEffect(() => {
    if (!user || user.client_type === 'ai') return undefined;
    const ping = () => {
      const live = typeof window !== 'undefined' && window.__spinItUpIsLive === true;
      axios.post(`${API}/analytics/heartbeat`, { is_playing: live }, { withCredentials: true }).catch(() => {});
    };
    ping();
    // Web-Worker interval so the hours heartbeat keeps firing even when the
    // tab is backgrounded or the tablet screen is off. Audio plays in the
    // background (hardened), so usage MUST be counted there too — a plain
    // setInterval is skipped/throttled while hidden and under-counted
    // "Hours online" to a fraction of real play time (venues playing all
    // morning showed only the minutes their tab was in the foreground).
    const handle = createBackgroundInterval(ping, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      handle.clear();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);
  useEffect(() => {
    if (!user) return undefined;
    const send = () => {
      const s = heartbeatRef.current;
      const vid = s.currentTrack?.id || null;
      if (!vid) return;   // nothing playing → no heartbeat
      const t = s.currentTrack || {};
      axios.post(`${API}/playback/heartbeat`, {
        is_playing: !!s.isPlaying,
        progress_ms: Math.max(0, Math.floor(s.progressMs || 0)),
        video_id: vid,
        now_playing: {
          name: t.name || t.title || '',
          artists: t.artists || (t.artist_name ? [t.artist_name] : []),
          album_image: t.album_image || t.art || '',
        },
      }).catch(() => { /* silent — heartbeat is best-effort */ });
    };
    const id = setInterval(send, 2000);
    send(); // send immediately on mount so the TV syncs fast
    return () => clearInterval(id);
  }, [user]);

  // --- Stability watchdogs (Feb 2026) --------------------------------------
  // Three narrow auto-recoveries for the silent-failure modes forensics
  // keeps surfacing. See utils/stabilityWatchdogs.js for the whole story.
  //   1. Mid-stream silent pause → auto-resume (3 strikes → skip)
  //   2. Wake-lock loss instrumentation (re-acquire lives in useScreenWakeLock)
  //   3. Network drop → auto-resume on `online`
  // Wire up once the driver is initialised. Watchdogs read live state via
  // `heartbeatRef` which is always current.
  useEffect(() => {
    if (!playerReady || !driverRef.current) return undefined;
    const getState = () => heartbeatRef.current;
    const stopPause = startPlaybackWatchdog({
      driver: driverRef.current,
      getState,
      onAutoSkip: () => {
        // Advance the queue via the same path the natural track-end takes,
        // so backend + WS sibling clients stay in sync.
        toast.info('Skipped a stuck track');
        axios.post(`${API}/queue/next`).catch(() => { /* noop */ });
        try { driverRef.current?.next(); } catch { /* noop */ }
      },
    });
    const stopWake = startWakeLockLossLogger(getState);
    const stopNet = startNetworkRecovery({ driver: driverRef.current, getState });
    return () => { stopPause(); stopWake(); stopNet(); };
  }, [playerReady]);

  // ---- OS / lock-screen media controls (MediaSession, June 2026) ---------
  // Registering a MediaSession signals the OS this is a real media app,
  // which helps our audio survive screen-lock/background on Android, and
  // surfaces lock-screen play/pause/skip controls. Handlers proxy to the
  // same controls the in-app buttons use.
  const mediaActionsRef = useRef({});
  useEffect(() => {
    mediaActionsRef.current = { play, pause, next: nextTrack, prev: previousTrack };
  });
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    try {
      ms.setActionHandler('play', () => mediaActionsRef.current.play?.());
      ms.setActionHandler('pause', () => mediaActionsRef.current.pause?.());
      ms.setActionHandler('nexttrack', () => mediaActionsRef.current.next?.());
      ms.setActionHandler('previoustrack', () => mediaActionsRef.current.prev?.());
    } catch { /* some handlers unsupported on this platform */ }
  }, []);
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    if (!currentTrack) return;
    try {
      const artists = Array.isArray(currentTrack.artists)
        ? currentTrack.artists.join(', ')
        : (currentTrack.artists || currentTrack.artist_name || '');
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentTrack.name || 'Now Playing',
        artist: artists || 'MusicBooth',
        album: currentTrack.album || '',
        artwork: currentTrack.album_image
          ? [{ src: currentTrack.album_image, sizes: '512x512', type: 'image/jpeg' }]
          : [],
      });
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    } catch { /* noop */ }
  }, [currentTrack, isPlaying]);

  // Signed out — release audio focus + clear the OS media session so nothing
  // lingers on the lock screen after logout.
  useEffect(() => {
    if (user) return;
    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = 'none';
      } catch { /* noop */ }
    }
  }, [user]);

  const value = {
    // core state
    playerReady,
    isPlaying,
    currentTrack,
    progressMs,
    durationMs,
    showPlayer,
    setShowPlayer,
    queue,
    queueIndex,
    queuePlaylistId,
    nextUp,
    crossfadeActive,
    activeSlot,
    volume,
    adPlaying,

    // controls
    play,
    pause,
    nextTrack,
    previousTrack,
    setVolume,
    playTracks,
    injectNext,
    refreshQueue,
    // Alias — some pages (e.g. PlaylistPage after drag-reorder or add-
    // track) call `syncQueue(newTracks)` expecting the queue to
    // re-hydrate. We optimistically stamp the local queue with what
    // they passed AND kick a backend refresh so the two stay honest.
    // Feb 2026 — closes the "syncQueue is not a function" TypeError.
    syncQueue: (newTracks) => {
      if (Array.isArray(newTracks)) setQueueState(newTracks);
      refreshQueue();
    },

    // compat stubs (formerly Apple MusicKit) — retained so legacy pages
    // that call these don't crash. These return no-op values.
    isAuthorized: true,        // YouTube has no per-user auth
    autoDJ: true,
    setAutoDJ: () => {},
    crossfader: 0.5,
    setCrossfader: () => {},
    // DJ Console slot mirrors
    slotATrack: activeSlot === 'A' ? currentTrack : nextUp,
    slotBTrack: activeSlot === 'B' ? currentTrack : nextUp,
    slotAState: activeSlot === 'A' && isPlaying ? 'playing' : 'idle',
    slotBState: activeSlot === 'B' && isPlaying ? 'playing' : 'idle',
    slotAProgress: activeSlot === 'A' ? progressMs : 0,
    slotBProgress: activeSlot === 'B' ? progressMs : 0,
    slotADuration: activeSlot === 'A' ? durationMs : 0,
    slotBDuration: activeSlot === 'B' ? durationMs : 0,
  };

  return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
};

export const useMusic = () => {
  const ctx = useContext(MusicContext);
  if (!ctx) throw new Error('useMusic must be used within a MusicProvider');
  return ctx;
};
